import type { ProductCenterProductsResponse } from "@adpilot/shared";
import type {
  PerformanceKpi,
  PerformanceTrendPoint,
} from "@/components/shared/performance/performance";

export const PRODUCT_CENTER_METRICS = [
  "totalSales",
  "adSales",
  "units",
  "spend",
  "orders",
  "acos",
  "tacos",
  "sessions",
] as const;
export type ProductCenterMetric = (typeof PRODUCT_CENTER_METRICS)[number];
export const PRODUCT_CENTER_DEFAULT_METRICS: ProductCenterMetric[] = [
  "totalSales",
  "adSales",
  "units",
  "spend",
  "tacos",
  "acos",
];

export function buildProductPerformance(
  data: ProductCenterProductsResponse | undefined,
  t: (key: string) => string,
): {
  kpis: PerformanceKpi<ProductCenterMetric>[];
  trend: PerformanceTrendPoint<ProductCenterMetric>[];
} {
  if (!data) return { kpis: [], trend: [] };
  const sellerDescription = t("performance.sellerUnavailable");
  const adsAvailable = data.summary.ads.status !== "unavailable";
  const tacosEligible = data.tacosEligibility.status === "available";
  const values: Record<ProductCenterMetric, number | null> = {
    totalSales: data.summary.seller.sales,
    adSales: adsAvailable ? data.summary.ads.adSales : null,
    units: data.summary.seller.units,
    spend: adsAvailable ? data.summary.ads.spend : null,
    orders: adsAvailable ? data.summary.ads.orders : null,
    acos: adsAvailable ? data.summary.ads.acos : null,
    tacos:
      tacosEligible && data.summary.seller.status === "available" && data.summary.seller.sales > 0
        ? data.summary.ads.spend / data.summary.seller.sales
        : null,
    sessions: data.summary.seller.sessions,
  };
  const formats: Record<ProductCenterMetric, "currency" | "number" | "percent"> = {
    totalSales: "currency",
    adSales: "currency",
    units: "number",
    spend: "currency",
    orders: "number",
    acos: "percent",
    tacos: "percent",
    sessions: "number",
  };
  const kpis = PRODUCT_CENTER_METRICS.map((key) => ({
    key,
    label: t(`performance.metrics.${key}`),
    value: values[key],
    previousValue: null,
    change: null,
    direction: "flat" as const,
    semantics: "neutral" as const,
    format: formats[key],
    currency:
      key === "totalSales" || key === "adSales" || key === "spend" ? data.currency : undefined,
    description:
      key === "tacos" && data.tacosEligibility.status === "unavailable"
        ? t(`performance.tacosUnavailable.${data.tacosEligibility.reason}`)
        : (key === "adSales" || key === "spend" || key === "orders" || key === "acos") &&
            data.summary.ads.status !== "available"
          ? t(
              data.summary.ads.status === "partial"
                ? "performance.adsPartialValue"
                : "performance.adsUnavailable",
            )
          : data.summary.seller.status === "unavailable" &&
              (key === "totalSales" || key === "units" || key === "tacos" || key === "sessions")
            ? sellerDescription
            : undefined,
  }));
  const trend = data.trend.map((point) => {
    const metricDataStatus: Partial<
      Record<ProductCenterMetric, "complete" | "partial" | "missing">
    > = {};
    if (point.ads.status !== "available") {
      const status = point.ads.status === "partial" ? "partial" : "missing";
      for (const key of ["adSales", "spend", "orders", "acos"] as const)
        metricDataStatus[key] = status;
    }
    if (point.seller.status !== "available") {
      const status = point.seller.status === "partial" ? "partial" : "missing";
      for (const key of ["totalSales", "units", "tacos", "sessions"] as const)
        metricDataStatus[key] = status;
    }
    return {
      periodStart: point.date,
      periodEnd: point.date,
      dataStatus: "complete" as const,
      totalSales: point.seller.sales,
      adSales: point.ads.status === "unavailable" ? null : point.ads.adSales,
      units: point.seller.units,
      spend: point.ads.status === "unavailable" ? null : point.ads.spend,
      orders: point.ads.status === "unavailable" ? null : point.ads.orders,
      acos: point.ads.status === "unavailable" ? null : point.ads.acos,
      tacos:
        tacosEligible && point.seller.status === "available" && point.seller.sales > 0
          ? point.ads.spend / point.seller.sales
          : null,
      sessions: point.seller.sessions,
      metricDataStatus: Object.keys(metricDataStatus).length > 0 ? metricDataStatus : undefined,
    };
  });
  return { kpis, trend };
}
